import { useEffect, type ReactNode } from 'react';

import { BootingScreen } from '@/components/booting-screen';
import { useAppDispatch, useAppSelector } from '@/context/store/hooks';
import {
  setLoading,
  setUnauthenticated,
  setUser,
} from '@/context/store/slices/authSlice';
import { apiClient } from '@/lib/api-client';
import { scheduleSilentRefresh } from '@/lib/silent-refresh';
import { tokenStorage } from '@/lib/token-storage';

import { AuthContext } from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const status = useAppSelector(s => s.auth.status);

  useEffect(() => {
    const tokens = tokenStorage.getAll();
    if (!tokens) {
      dispatch(setUnauthenticated());
      return;
    }

    dispatch(setLoading());
    scheduleSilentRefresh(tokens.accessToken);

    void (async () => {
      const { data, error } = await apiClient.GET('/users/me');
      if (error || !data) {
        tokenStorage.clearAll();
        dispatch(setUnauthenticated());
      } else {
        dispatch(setUser(data));
      }
    })();
  }, [dispatch]);

  if (status === 'idle' || status === 'loading') {
    return <BootingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: status === 'authenticated' }}
    >
      {children}
    </AuthContext.Provider>
  );
}
